import { calculerBulletin } from '@domain/paie/CalculBulletin';
import { parametresPaieEntreprise } from '@domain/paie/ContributionsEmployeur';
import { generateEcrituresPaie } from '@domain/comptabilite/EcrituresPaieGenerator';
import type { TableComptesPaie } from '@domain/comptabilite/ComptesPaie';
import { premierePeriodeOuverte } from '@domain/comptabilite/ExtournePaie';
import type { Salarie } from '@shared/types/Salarie';
import type { TenantSettings } from '@shared/types/Tenant';
import type { Bulletin } from '@shared/types/Bulletin';
import type { Ecriture } from '@shared/types/Ecriture';
import { SaisieBulletinSchema } from '@shared/validation/BulletinSchema';
import { cumulExoArt9111Anterieur } from '@domain/paie/ExonerationArt9111';

export interface GenererBulletinCommand {
  salarie: Salarie;
  saisie: unknown;
  periode: string;
  /** Settings tenant (`parametres` / `settings`) pour surcharge fiscale. */
  tenantSettings?: Record<string, unknown> | TenantSettings | null;
  /**
   * Forme juridique de l'entreprise : sert à déduire son statut fiscal (donc l'application par défaut de la CFP) quand
   * l'entreprise n'a pas choisi de statut. Absente = statut « autre ».
   */
  formeJuridique?: string | null;
  /** Table de correspondance des comptes de paie de l'entreprise (défaut : valeurs documentées). */
  comptesPaie?: TableComptesPaie;
  /**
   * Périodes comptables clôturées. Si la période du bulletin en fait partie, les écritures sont imputées
   * sur la première période ouverte (`periode_origine` = période du bulletin), jamais dans la période clôturée.
   */
  periodesCloturees?: readonly string[];
  /**
   * Bulletins déjà générés (tous salariés ou ceux du salarié) : sert au cumul ANNUEL de l'exonération des primes
   * de l'art. 91 11° (plafond de 4 000 000 FCFA par année civile). Absent = aucun bulletin antérieur.
   */
  bulletinsExistants?: readonly Bulletin[];
}

export interface GenererBulletinResult {
  bulletin: Bulletin;
  ecritures: Ecriture[];
}

/**
 * Use Case : génération d'un bulletin et de ses écritures comptables associées.
 * Orchestre validation + calcul + génération écritures.
 */
export class GenererBulletinUseCase {
  execute(cmd: GenererBulletinCommand): GenererBulletinResult {
    const saisie = SaisieBulletinSchema.parse(cmd.saisie);
    if ((saisie.avance ?? 0) > cmd.salarie.salaire_base) {
      throw new Error('L’avance ne peut pas dépasser le salaire de base');
    }
    // Paramètres de la PÉRIODE du bulletin (table datée) puis réglages de l'entreprise (FNH / CFP).
    const params = parametresPaieEntreprise(
      { forme_juridique: cmd.formeJuridique, parametres: cmd.tenantSettings },
      cmd.periode,
    );
    const bulletin = calculerBulletin(cmd.salarie, saisie, {
      periode: cmd.periode,
      params,
      exoArt9111Anterieur: cumulExoArt9111Anterieur(
        cmd.bulletinsExistants ?? [],
        cmd.salarie.id,
        cmd.periode,
      ),
    });
    const ecritures = generateEcrituresPaie(
      bulletin,
      cmd.salarie,
      cmd.comptesPaie,
      cmd.periodesCloturees?.includes(cmd.periode)
        ? premierePeriodeOuverte(cmd.periode, cmd.periodesCloturees)
        : undefined,
    );
    return { bulletin, ecritures };
  }
}
